import React from 'react';
import { Box, Typography, TextField, IconButton, Button } from '@mui/material';
import { Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import InlineAlert from '../../../../shared/components/InlineAlert';
import type { WebhookNotificationTarget } from '../../../../types/frontend';

interface WebhookTargetCardProps {
  target: WebhookNotificationTarget;
  onChange: (target: WebhookNotificationTarget) => void;
  onRemove: () => void;
  hasAttemptedSave: boolean;
  urlError?: string;
  disabled?: boolean;
}

/**
 * Frontend-only webhook delivery. Backend has no webhook channel yet, so a rule
 * carrying one cannot be saved — the editor blocks save and shows why. URL +
 * optional auth headers are captured now so the UX is ready when the backend lands.
 */
const WebhookTargetCard: React.FC<WebhookTargetCardProps> = ({
  target,
  onChange,
  onRemove,
  hasAttemptedSave,
  urlError,
  disabled,
}) => {
  const patch = (changes: Partial<WebhookNotificationTarget>) => {
    onChange({ ...target, ...changes });
  };

  const setHeader = (index: number, key: string, value: string) => {
    const headers = [...target.headers];
    headers[index] = { key, value };
    patch({ headers });
  };

  return (
    <Box sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, pt: 1, pb: 0.5 }}>
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          Webhook
        </Typography>
        <IconButton size="small" onClick={onRemove} disabled={disabled}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <InlineAlert severity="info">
          Webhook delivery isn’t available yet — the backend is coming soon. You can
          configure it now, but the rule can’t be saved while a webhook is present.
        </InlineAlert>

        <TextField
          fullWidth
          required
          size="small"
          label="URL"
          value={target.url}
          onChange={(e) => patch({ url: e.target.value })}
          error={hasAttemptedSave && !!urlError}
          helperText={(hasAttemptedSave && urlError) || 'HTTPS endpoint the event POSTs to'}
          placeholder="https://hooks.example.com/opspilot"
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
        />

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Auth headers (optional)
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {target.headers.map((header, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  size="small"
                  label="Header"
                  value={header.key}
                  onChange={(e) => setHeader(idx, e.target.value, header.value)}
                  placeholder="Authorization"
                  disabled={disabled}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Value"
                  value={header.value}
                  onChange={(e) => setHeader(idx, header.key, e.target.value)}
                  placeholder="Bearer …"
                  disabled={disabled}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <IconButton
                  size="small"
                  onClick={() => patch({ headers: target.headers.filter((_, i) => i !== idx) })}
                  disabled={disabled}
                  aria-label="Remove header"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => patch({ headers: [...target.headers, { key: '', value: '' }] })}
            disabled={disabled}
            sx={{ mt: 1 }}
          >
            Add header
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default WebhookTargetCard;
